import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { PolicyRecord } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { formatISODate } from 'src/Utils/formatDate'
import { generatePermalink } from 'src/components/PageLayout/Permalink'
import {
  Grid,
  GridColumn,
  GridHeader,
  GridRow,
} from 'src/components/Reusable/Grid'
import { Title } from 'src/components/Reusable/Text/Title'
import { DepartmentChip } from 'src/pages/Controls/DepartmentChip'
import { OwnerAssignmentButton } from 'src/components/Reusable/Buttons'
import { StyledGridRow } from 'src/pages/Policies/PoliciesIndexPage/PoliciesIndexPage.styles'
import { useInvalidatePoliciesCache } from 'src/components/async/policy/usePolicies'

const GRID_COLUMNS = 'min-content 1fr repeat(4, max-content)'

export const ReceptorPoliciesGrid = ({
  policies,
}: {
  policies: PolicyRecord[]
}): JSX.Element => {
  if (policies.length === 0) {
    return (
      <Grid gridTemplateColumns="1fr">
        <GridRow>
          <GridColumn justifyContent="center">
            No policies have been brought in by folder scans from this receptor.
          </GridColumn>
        </GridRow>
      </Grid>
    )
  }

  return (
    <Grid gridTemplateColumns={GRID_COLUMNS}>
      <GridRow>
        <GridHeader>Department</GridHeader>
        <GridHeader>Name</GridHeader>
        <GridHeader>Last Reviewed</GridHeader>
        <GridHeader>Last Approved</GridHeader>
        <GridHeader>Controls</GridHeader>
        <GridHeader>Owner</GridHeader>
      </GridRow>
      {policies.map((policy) => (
        <ReceptorPolicyRow key={policy.getId()} policy={policy} />
      ))}
    </Grid>
  )
}

const ReceptorPolicyRow = ({
  policy,
}: {
  policy: PolicyRecord
}): JSX.Element => {
  const params = useParams()
  const invalidatePolicies = useInvalidatePoliciesCache()
  const modelId = policy.getModelId()
  const lastReviewedDate = formatISODate(policy.getLastReviewedDate()) || ''
  const lastApprovedDate = formatISODate(policy.getLastApprovedDate()) || ''

  return (
    <StyledGridRow
      as={Link}
      to={generatePermalink({
        pageContext: params.pageContext as string,
        modelId,
        isInternalLink: true,
        includeSearchParams: false,
        modelType: MODEL_TYPE.POLICY,
      })}
    >
      <GridColumn>
        <DepartmentChip departmentId={policy.getDepartmentId()} />
      </GridColumn>
      <GridColumn>
        <Title name={policy.getName()} />
      </GridColumn>
      <GridColumn>{lastReviewedDate}</GridColumn>
      <GridColumn>{lastApprovedDate}</GridColumn>
      <GridColumn>{policy.getControlsCount()}</GridColumn>
      <GridColumn>
        <OwnerAssignmentButton
          modelId={modelId}
          modelType={MODEL_TYPE.POLICY}
          email={policy.getOwnerEmail()}
          isDisabled={policy.getIsDismissed()}
          onUpdate={() => invalidatePolicies()}
        />
      </GridColumn>
    </StyledGridRow>
  )
}
